/**
 * Store de autenticación biométrica con Zustand
 * Sprint 7 - Login con huella digital
 *
 * Maneja estado global de:
 * - Disponibilidad de biometría en el dispositivo
 * - Si el usuario habilitó login biométrico
 * - Flujo de autenticación biométrica
 * - Prompt post-login para habilitar
 */

import { create } from 'zustand';
import { biometricService, getBiometryName } from '../services/biometric';
import { BiometricAuth } from '@aparajita/capacitor-biometric-auth';
import type { BiometryType, CheckBiometryResult } from '@aparajita/capacitor-biometric-auth';
import type { PluginListenerHandle } from '@capacitor/core';

// Sprint 8: Referencia al listener de resume para poder limpiarlo
let resumeListenerHandle: PluginListenerHandle | null = null;

interface BiometricState {
    // Estado
    isAvailable: boolean;
    biometryType: BiometryType | null;
    isEnabled: boolean;
    hasCredentials: boolean;
    isAuthenticating: boolean;
    error: string | null;

    // UI flags
    showEnablePrompt: boolean;
    isInitialized: boolean;

    // Acciones
    initialize: () => Promise<void>;
    authenticateAndGetToken: () => Promise<{ email: string; token: string } | null>;
    enableBiometric: (email: string, token: string) => Promise<void>;
    disableBiometric: () => Promise<void>;
    updateToken: (token: string) => Promise<void>;
    showPrompt: () => void;
    hidePrompt: () => void;
    clearError: () => void;
}

export const useBiometricStore = create<BiometricState>((set, get) => ({
    // Estado inicial
    isAvailable: false,
    biometryType: null,
    isEnabled: false,
    hasCredentials: false,
    isAuthenticating: false,
    error: null,
    showEnablePrompt: false,
    isInitialized: false,

    /**
     * Inicializa: verifica disponibilidad y estado del biométrico
     * Se llama al montar la pantalla de login
     */
    initialize: async () => {
        try {
            const status = await biometricService.getStatus();

            set({
                isAvailable: status.isAvailable,
                biometryType: status.biometryType,
                isEnabled: status.isEnabled,
                hasCredentials: status.hasCredentials,
                isInitialized: true,
            });

            if (status.isAvailable) {
                const name = getBiometryName(status.biometryType);
                console.log(`[BiometricStore] Available: ${name}`);
            }

            // Sprint 8: Registrar resume listener para detectar cambios en disponibilidad
            // (ej: usuario desactiva huella en ajustes del sistema)
            if (resumeListenerHandle) {
                await resumeListenerHandle.remove();
                resumeListenerHandle = null;
            }

            try {
                resumeListenerHandle = await BiometricAuth.addResumeListener(
                    (info: CheckBiometryResult) => {
                        set({
                            isAvailable: info.isAvailable,
                            biometryType: info.biometryType,
                        });
                        console.log('[BiometricStore] Resume: availability updated:', info.isAvailable);
                    }
                );
            } catch (listenerError) {
                console.warn('[BiometricStore] Could not add resume listener:', listenerError);
            }
        } catch (error) {
            console.warn('[BiometricStore] Init error:', error);
            set({ isInitialized: true });
        }
    },

    /**
     * Realiza autenticación biométrica y retorna credenciales guardadas
     * Se llama cuando el usuario toca el botón de huella
     */
    authenticateAndGetToken: async () => {
        const { isAvailable, isEnabled, hasCredentials } = get();

        if (!isAvailable || !isEnabled || !hasCredentials) {
            return null;
        }

        set({ isAuthenticating: true, error: null });

        try {
            // Prompt biométrico nativo
            const authenticated = await biometricService.authenticate();

            if (!authenticated) {
                set({ isAuthenticating: false });
                return null;
            }

            // Biométrico OK → recuperar credenciales
            const credentials = await biometricService.getStoredCredentials();

            if (!credentials) {
                set({
                    isAuthenticating: false,
                    error: 'No se encontraron credenciales guardadas',
                    hasCredentials: false,
                    isEnabled: false,
                });
                return null;
            }

            set({ isAuthenticating: false });
            return credentials;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Error de autenticación';
            set({ isAuthenticating: false, error: message });
            return null;
        }
    },

    /**
     * Habilita login biométrico guardando credenciales
     * Se llama cuando el usuario acepta el prompt post-login
     */
    enableBiometric: async (email: string, token: string) => {
        try {
            await biometricService.saveCredentials(email, token);
            set({
                isEnabled: true,
                hasCredentials: true,
                showEnablePrompt: false,
            });
        } catch (error) {
            console.error('[BiometricStore] Error enabling:', error);
            set({ showEnablePrompt: false });
        }
    },

    /**
     * Deshabilita y limpia credenciales biométricas
     */
    disableBiometric: async () => {
        await biometricService.clearCredentials();
        set({
            isEnabled: false,
            hasCredentials: false,
        });
    },

    /**
     * Actualiza el token almacenado (ej. tras refresh)
     */
    updateToken: async (token: string) => {
        await biometricService.updateStoredToken(token);
    },

    /**
     * Muestra el prompt para habilitar biométrico (post-login)
     */
    showPrompt: () => set({ showEnablePrompt: true }),

    /**
     * Oculta el prompt sin habilitar
     */
    hidePrompt: () => set({ showEnablePrompt: false }),

    /**
     * Limpia errores
     */
    clearError: () => set({ error: null }),
}));
